<template>
	<Modal
		:visible="visible"
		:title="$t('account.externalApiKeys.updatePermissionsModal.header')"
		:ok-text="$t('account.externalApiKeys.updatePermissionsModal.saveButtonLabel')"
		:cancel-text="$t('account.externalApiKeys.cancelButtonLabel')"
		:confirm-loading="isUpdatingPermissions"
		@ok="onUpdatePermissions"
		@cancel="closeModal"
	>
		<Form layout="vertical">
			<ExternalApiKeyPermissionsEditor
				v-model:permissions="form.permissions"
				:domain-error-visible="domainErrorVisible"
			/>
		</Form>
	</Modal>
</template>

<script>
import { reactive, ref, watch } from 'vue';
import { Form, Modal, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import { showErrorMessage } from '@/components/common/errorMessage';
import ExternalApiKeyPermissionsEditor from '@/components/account/ExternalApiKeyPermissionsEditor.vue';
import { hasDomainPermission } from '@/components/account/externalApiKeyPermissions';


export default {
	components: {
		Form,
		Modal,
		ExternalApiKeyPermissionsEditor,
	},
	props: {
		visible: {
			type: Boolean,
			required: true,
		},
		apiKey: {
			type: Object,
			required: false,
			default: null,
		},
	},
	emits: ['close', 'updated'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const isUpdatingPermissions = ref(false);
		const domainErrorVisible = ref(false);
		const form = reactive({
			id: null,
			permissions: [],
		});

		watch(() => props.visible, isVisible => {
			if (!isVisible) {
				return;
			}
			form.id = props.apiKey?.id ?? null;
			form.permissions = [...(props.apiKey?.permissions || [])];
			domainErrorVisible.value = false;
		});

		watch(() => form.permissions, () => {
			if (domainErrorVisible.value && hasDomainPermission(form.permissions)) {
				domainErrorVisible.value = false;
			}
		}, { deep: true });

		const closeModal = () => {
			isUpdatingPermissions.value = false;
			domainErrorVisible.value = false;
			form.id = null;
			form.permissions = [];
			emit('close');
		};

		const onUpdatePermissions = () => {
			if (form.permissions.length === 0) {
				showErrorMessage({
					permissions: [t('account.externalApiKeys.updatePermissionsModal.permissionsRequired')],
				});
				return;
			}
			if (!hasDomainPermission(form.permissions)) {
				domainErrorVisible.value = true;
				return;
			}
			if (form.id == null) {
				showErrorMessage({
					non_field_errors: [t('common.unknownError')],
				});
				return;
			}
			domainErrorVisible.value = false;
			isUpdatingPermissions.value = true;
			Api.externalApiKeyUpdatePermissions(form.id, form.permissions, response => {
				isUpdatingPermissions.value = false;
				if (response.status === 'ok') {
					message.success(t('account.externalApiKeys.updatePermissionsSuccess'));
					emit('updated');
					closeModal();
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		return {
			isUpdatingPermissions,
			form,
			domainErrorVisible,
			closeModal,
			onUpdatePermissions,
		};
	},
};
</script>
